/**
 * Referring-domain CSV parsing, shared by Add Project, Affiliate Gap and
 * Competitor Spy.
 *
 * The file is a SEMrush "backlinks_refdomains" export. We send the rows as
 * JSON (not multipart) because the backend pipeline already consumes that
 * shape: `collectDomains({ csvBacklinks, skipSemrush })` reads
 * domain / backlinks_num / ip / country off each row, keyed by lower-cased
 * header. Anything else in the file rides along untouched and is ignored.
 *
 * Uploading is admin-only. That is enforced on the SERVER (each route re-checks
 * the admin email before honouring csvBacklinks) — the client-side gate here is
 * only so the control isn't shown to people who can't use it.
 */

// Header names we accept for the one column that actually matters.
const DOMAIN_HEADERS = ['domain', 'backlink_url', 'url', 'referring domain'];

const stripQuotes = (value) => String(value ?? '').trim().replace(/^"|"$/g, '');

/**
 * @param {string} text raw file contents
 * @returns {Array<Object>} one object per row, keys lower-cased headers
 */
export const parseReferralCsv = (text) => {
  const lines = String(text || '').split('\n').filter((line) => line.trim());
  if (lines.length < 2) return [];

  // SEMrush exports semicolon-delimited; hand-made files are usually comma or
  // tab. Sniff the header rather than making the admin pick.
  const header = lines[0];
  let separator = ',';
  if (header.includes('\t')) separator = '\t';
  else if (header.includes(';')) separator = ';';

  const headers = header.split(separator).map((h) => stripQuotes(h).toLowerCase());
  const domainIndex = headers.findIndex((h) => DOMAIN_HEADERS.includes(h));
  if (domainIndex === -1) return [];

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(separator);
    const domain = stripQuotes(values[domainIndex]);
    if (!domain) continue;

    const row = {};
    headers.forEach((name, idx) => { row[name] = stripQuotes(values[idx]); });
    rows.push(row);
  }
  return rows;
};

/**
 * Read a File and parse it. Resolves { rows, error } — never rejects, so the
 * caller can put `error` straight on screen.
 */
export const readReferralCsv = (file) =>
  new Promise((resolve) => {
    if (!file) {
      resolve({ rows: [], error: 'No file selected' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      resolve({ rows: [], error: 'Please choose a .csv file' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const rows = parseReferralCsv(event.target.result);
      if (rows.length === 0) {
        resolve({
          rows: [],
          error: 'Could not read that CSV. It needs a header row with a "Domain" column.'
        });
        return;
      }
      resolve({ rows, error: null });
    };
    reader.onerror = () => resolve({ rows: [], error: 'Could not read that file' });
    reader.readAsText(file);
  });

export const REFERRAL_CSV_HINT = 'CSV needs a "Domain" column (a SEMrush referring-domains export works as-is)';
